// src/components/FittingCalendar.js

import React, { useState, useEffect, useRef } from "react";
import "../style/FittingCalendar.css";
import { useTranslation } from "react-i18next";

// Cloud Function URL
const CLOUD_FUNCTION_URL =
  "https://us-central1-mcknight-homme.cloudfunctions.net/getCalendlyAvailability";

const FittingCalendar = ({ onConfirm }) => {
  const { t, i18n } = useTranslation();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availabilityData, setAvailabilityData] = useState({});
  const [eventTypeUri, setEventTypeUri] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedDatesRef = useRef(new Set()); // Track which dates we've fetched

  // Format date key for availability lookup (YYYY-MM-DD)
  const getDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch availability from Cloud Function
  const fetchAvailability = async (startDate, forceRefresh = false) => {
    const startKey = getDateKey(startDate);

    // Calculate end date (6 days from start)
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    const endKey = getDateKey(endDate);

    // Check if we already have data for this start date
    if (!forceRefresh && fetchedDatesRef.current.has(startKey)) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${CLOUD_FUNCTION_URL}?startDate=${startKey}&endDate=${endKey}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch availability");
      }

      const data = await response.json();

      if (data.success) {
        // Merge new availability with existing data
        setAvailabilityData((prev) => ({
          ...prev,
          ...data.availability,
        }));
        setEventTypeUri(data.eventType?.uri);

        // Mark this start date as fetched
        fetchedDatesRef.current.add(startKey);

        // console.log("Fetched availability:", data.debug);
      } else {
        throw new Error(data.error || "Unknown error");
      }
    } catch (err) {
      //   console.error("Error fetching availability:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch - starting from today
  useEffect(() => {
    const today = new Date();
    fetchAvailability(today);
  }, []);

  const formatDateDisplay = (date) => {
    const dayName = date
      .toLocaleDateString(i18n.language, { weekday: "short" })
      .toLowerCase();
    const dayNum = date.getDate();
    const monthName = date
      .toLocaleDateString(i18n.language, { month: "short" })
      .toLowerCase()
      .replace(".", "");
    const year = date.getFullYear();

    return `${dayName} ${dayNum} ${monthName} . ${year}`;
  };

  const formatConfirmationDate = (date) => {
    const options = {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    let dateString = date.toLocaleDateString(i18n.language, options);
    // Capitalize first letter
    dateString = dateString.charAt(0).toUpperCase() + dateString.slice(1);
    return dateString;
  };

  // Navigate to previous day
  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);

    // Don't go before today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (newDate >= today) {
      setSelectedDate(newDate);
      setSelectedSlot(null);

      // Check if we have data for this date
      const dateKey = getDateKey(newDate);
      if (!availabilityData[dateKey] && !fetchedDatesRef.current.has(dateKey)) {
        fetchAvailability(newDate);
      }
    }
  };

  // Navigate to next day
  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
    setSelectedSlot(null);

    // Check if we need to fetch more data
    const dateKey = getDateKey(newDate);
    if (!availabilityData[dateKey]) {
      // Fetch starting from this new date
      fetchAvailability(newDate);
    }
  };

  // Get available slots for selected date
  const dateKey = getDateKey(selectedDate);
  const availableSlots = availabilityData[dateKey] || [];

  // Format slot for display: "14:00 - 14:30"
  const formatSlotDisplay = (startTime) => {
    const [hours, minutes] = startTime.split(":").map(Number);
    let endHours = hours;
    let endMinutes = minutes + 30;

    if (endMinutes >= 60) {
      endMinutes = 0;
      endHours += 1;
    }

    const endTime = `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
    return `${startTime} - ${endTime}`;
  };

  // Handle slot selection
  const handleSlotClick = (slotData) => {
    setSelectedSlot(slotData);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (selectedSlot && onConfirm) {
      onConfirm({
        date: selectedDate,
        slot: selectedSlot.time,
        startTime: selectedSlot.startTime,
        endSlot: formatSlotDisplay(selectedSlot.time).split(" - ")[1],
        eventTypeUri: eventTypeUri,
      });
    }
  };

  // Loading state (only show full loading on initial load)
  if (isLoading && Object.keys(availabilityData).length === 0) {
    return (
      <div className="fitting-calendar">
        <div className="fc-loading">
          <div className="fc-spinner"></div>
          <p>{t("Loading available times...")}</p>
        </div>
      </div>
    );
  }

  // Error state (only show if no data at all)
  if (error && Object.keys(availabilityData).length === 0) {
    return (
      <div className="fitting-calendar">
        <div className="fc-error">
          <p>{t("Unable to load available times.")}</p>
          <button onClick={() => fetchAvailability(new Date(), true)}>
            {t("Try Again")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fitting-calendar">
      {/* Date Navigation Header */}
      <div className="fc-date-header">
        <button className="fc-nav-button" onClick={handlePrevDay}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18L9 12L15 6"
              stroke="#707070"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="fc-date-display">
          <span className="fc-date-text">
            {formatDateDisplay(selectedDate)}
          </span>
          <span className="fc-timezone-divider">|</span>
          <span className="fc-timezone">CET</span>
        </div>

        <button className="fc-nav-button" onClick={handleNextDay}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 18L15 12L9 6"
              stroke="#707070"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Time Slots Grid */}
      <div className="fc-slots-grid">
        {isLoading ? (
          <div className="fc-slots-loading">
            <div className="fc-spinner-small"></div>
          </div>
        ) : availableSlots.length === 0 ? (
          <div className="fc-no-slots">
            <p>{t("No available times on this date.")}</p>
            <p className="fc-no-slots-hint">
              {t("Try selecting another day.")}
            </p>
          </div>
        ) : (
          availableSlots.map((slotData) => {
            const selected = selectedSlot?.time === slotData.time;

            return (
              <button
                key={slotData.time}
                className={`fc-slot available ${selected ? "selected" : ""}`}
                onClick={() => handleSlotClick(slotData)}
              >
                {formatSlotDisplay(slotData.time)}
              </button>
            );
          })
        )}
      </div>

      {/* Confirmation Section */}
      {selectedSlot && (
        <div className="fc-confirmation-section">
          <p className="fc-confirmation-text">
            {t("Video call for fitting.")}{" "}
            {formatConfirmationDate(selectedDate)}. Paris Time
          </p>

          <button className="fc-confirm-button" onClick={handleConfirm}>
            {t("Confirm The Appointment")}
          </button>
        </div>
      )}
    </div>
  );
};

export default FittingCalendar;
